import { createContext, ReactNode, useContext, useState } from 'react';
import TagManager from 'react-gtm-module';
import { logger } from '~utils/logger';
import { isAnalyticalCookieEnabled } from './cookies';
import { AnalyticsEvents, EventType } from './events';

declare global {
  interface Window {
    TPV_ANALYTICS_DEBUG?: boolean;
  }
}

const GTM_ID = process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID;

export interface PageAnalyticsAttributes {
  name?: string;
  disableTracking?: boolean;
}

export const AnalyticsContext = createContext<{
  pageAttributes: PageAnalyticsAttributes | null;
  setPageAttributes: (attributes: PageAnalyticsAttributes) => void;
} | null>(null);

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const [pageAttributes, setPageAttributes] = useState<PageAnalyticsAttributes | null>(null);

  return (
    <AnalyticsContext.Provider value={{ pageAttributes, setPageAttributes }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function usePageAnalytics() {
  const ctx = useContext(AnalyticsContext);

  if (!ctx) {
    throw new Error('usePageAnalytics must be used inside a AnalyticsProvider.');
  }

  return ctx;
}

export const useAnalyticsReporter = () => {
  const { pageAttributes } = usePageAnalytics();

  const initialize = () => {
    // The user doesn't want to be tracked.
    if (!isAnalyticalCookieEnabled()) {
      return;
    }

    if (GTM_ID) {
      TagManager.initialize({ gtmId: GTM_ID });
    }

    // Note: TagManager actually doesn't have an "uninitialize" method so
    // even if the user changes their settings, the TagManager is still kinda enabled.

    if (window.TPV_ANALYTICS_DEBUG ?? false) {
      logger.info('analytics initialized');
    }
  };

  const track = <T extends EventType>(
    event: T,
    ...eventDetails: AnalyticsEvents[T] extends undefined ? [undefined?] : [AnalyticsEvents[T]]
  ) => {
    // The page is not worthy of being tracked.
    if (pageAttributes?.disableTracking) {
      return;
    }

    // The user doesn't want to be tracked.
    // Let's be nice and send nothing.
    if (!isAnalyticalCookieEnabled()) {
      return;
    }

    // Use the pageName from the details if one is present
    let pageName = pageAttributes?.name;
    if (event === 'pageView') {
      pageName = (eventDetails[0] as AnalyticsEvents['pageView']).pageName;
    }

    const details = Object.assign({}, eventDetails, { pageName });

    if (window.TPV_ANALYTICS_DEBUG ?? false) {
      logger.info('analytics event reported', { event, details });
    }
  };

  return {
    track,
    initialize,
  };
};
